import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePermission } from '../contexts/PermissionContext';
import { isRouteActive } from '../utils/routeMatch';
import { buildNavigation } from './navigation';

export { isRouteActive };

const useNavigation = () => {
  const { menuTree, isMenuLoading } = usePermission();
  const { pathname } = useLocation();
  const navigation = useMemo(
    () => buildNavigation(menuTree, pathname),
    [menuTree, pathname]
  );
  return { navigation, isMenuLoading };
};

/** Group > module > page of the current route, or null (no sidebar match). */
export const useNavTrail = () => {
  const { pathname } = useLocation();
  const { navigation } = useNavigation();
  // Crude pages keep their own breadcrumbs.
  return /^\/crude(\/|$)/.test(pathname) ? null : navigation.trail;
};

// Groups start closed; the ones the user opens are remembered per browser.
const EXPANDED_KEY = 'hydro.nav.expandedGroups';

// Always open, no toggle.
const PINNED_GROUPS = ['overview'];

const readExpanded = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(EXPANDED_KEY));
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
};

const useMenuData = () => {
  const { navigation, isMenuLoading } = useNavigation();
  const { groups, trail } = navigation;
  const activeCode = trail?.module.id || '';
  const activeGroup = trail?.group.id || '';
  const [openCode, setOpenCode] = useState(activeCode);
  const [expanded, setExpanded] = useState(readExpanded);

  const persist = next => {
    try {
      localStorage.setItem(EXPANDED_KEY, JSON.stringify(next));
    } catch {
      // private mode / blocked storage: the choice just lasts this session
    }
    return next;
  };

  const toggleGroup = id =>
    setExpanded(prev =>
      persist(prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id])
    );

  // Landing in a closed group opens it, so the current page is never hidden.
  useEffect(() => {
    if (activeGroup) {
      setExpanded(prev =>
        prev.includes(activeGroup) ? prev : persist([...prev, activeGroup])
      );
    }
  }, [activeGroup]);

  // Follow the user to a new module; manual toggles stay until then.
  useEffect(() => {
    if (activeCode) setOpenCode(activeCode);
  }, [activeCode]);

  const menuItems = useMemo(
    () =>
      groups.flatMap(group => {
        const collapsible = !!group.label && !PINNED_GROUPS.includes(group.id);
        const isCollapsed = collapsible && !expanded.includes(group.id);
        return [
          ...(group.label
            ? [
                {
                  id: `grp-${group.id}`,
                  label: group.label,
                  isHeader: true,
                  isActive: group.isActive,
                  collapsible,
                  isCollapsed,
                  toggle: () => toggleGroup(group.id),
                },
              ]
            : []),
          ...group.items.map(node => ({
            id: node.id,
            label: node.label,
            icon: node.icon,
            link: node.link,
            isHeader: false,
            isGroupCollapsed: isCollapsed,
            isActive: node.isActive,
            hasActive: node.hasActive,
            stateVariables: openCode === node.id,
            click: e => {
              if (!node.subs.length) return;
              e.preventDefault();
              setOpenCode(prev => (prev === node.id ? '' : node.id));
            },
            ...(node.subs.length && { subItems: node.subs }),
          })),
        ];
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, openCode, expanded]
  );

  return { menuItems, isCurrentState: openCode, isMenuLoading };
};

export default useMenuData;
